import { inject } from "vue";
import { Properties } from "acquaint-api/models/Properties";
import { AcquaintApiClient } from "@/services/AcquaintApiClient";
import displayErrorMessage from "@/utils/ErrorHandling";
import { getErrorMessage } from "bright-logic-core/src/errors";
import { PropertySummary } from "@/types/properties/PropertySummary";
import { PropertyRooms } from "acquaint-api/models/PropertyRooms";
import { AcquaintApiNextClient } from "@/services/AcquaintApiNextClient";

export function usePropertyHelper() {
  const acquaintApiClient = inject<AcquaintApiClient>("AcquaintApiClient")!;
  const apiNextClient = inject<AcquaintApiNextClient>("AcquaintApiNextClient")!;

  return {
    fetchPropertyById: async (propertyId: number) => {
      try {
        if (propertyId > 0) {
          const property = await acquaintApiClient.httpGet<Properties>(
            `database/properties/${propertyId}`,
          );
          return property;
        }
      } catch (ex) {
        displayErrorMessage(getErrorMessage(ex));
      }
    },
    fetchPropertyDataSummary: async (propertyId: number) => {
      try {
        return await acquaintApiClient.httpGet<PropertySummary>(
          `app/properties/${propertyId}/summary`,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    updateProperty: async (propertyId: number, propertyData: Properties) => {
      try {
        return await acquaintApiClient.httpPut<Properties>(
          `database/properties/${propertyId}`,
          propertyData,
        );
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    movePicturePlace: async (
      recordId: number,
      oldIndex: number,
      newIndex: number,
    ) => {
      try {
        await acquaintApiClient.httpPostVoid(
          `app/properties/${recordId}/pictures/move`,
          {
            fromPictureNumber: oldIndex,
            toPictureNumber: newIndex,
          },
        );
        return true;
      } catch (err) {
        displayErrorMessage("We could not move the position of this picture.");
        return false;
      }
    },

    moveWorkOrdersPicturePlace: async (
      recordId: number,
      oldIndex: number,
      newIndex: number,
    ) => {
      try {
        await acquaintApiClient.httpPostVoid(
          `app/workorders/${recordId}/pictures/rearrange`,
          {
            fromPictureNumber: oldIndex,
            toPictureNumber: newIndex,
          },
        );
        return true;
      } catch (err) {
        displayErrorMessage("We could not move the position of this picture.");
        return false;
      }
    },

    deletePhoto: async (recordId: number, pictureNumber: number) => {
      try {
        await acquaintApiClient.httpDeleteVoid(
          `app/properties/${recordId}/pictures/${pictureNumber}`,
        );
        return true;
      } catch (err) {
        displayErrorMessage("We could not delete this picture.");
        return false;
      }
    },

    deleteWorkOrderPhoto: async (recordId: number, pictureNumber: number) => {
      try {
        await acquaintApiClient.httpDeleteVoid(
          `app/workorders/${recordId}/pictures/${pictureNumber}`,
        );
        return true;
      } catch (err) {
        displayErrorMessage("We could not delete this picture.");
        return false;
      }
    },

    submitImageToAPI: async (recordId: number, image: File) => {
      try {
        await acquaintApiClient.uploadFile(
          `app/properties/${recordId}/pictures`,
          image,
        );
        return true;
      } catch (err) {
        displayErrorMessage(
          "We could not upload your image, please ensure it is in a .jpg, .jpeg or .gif formats.",
        );

        return false;
      }
    },

    submitWorkOrderImageToAPI: async (recordId: number, image: File) => {
      try {
        await acquaintApiClient.uploadFile(
          `app/workorders/${recordId}/pictures`,
          image,
        );
        return true;
      } catch (err) {
        displayErrorMessage(
          "We could not upload your image, please ensure it is in a .jpg, .jpeg or .gif formats.",
        );

        return false;
      }
    },

    lockProperty: async (propertyId: number, lock: boolean) => {
      try {
        await acquaintApiClient.httpPostVoid(
          `database/properties/${propertyId}/lock`,
          {
            lock: lock,
            extraInfo: "",
          },
        );
        return false;
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
        return true;
      }
    },

    createPropertyRoom: async (room: PropertyRooms) => {
      try {
        await acquaintApiClient.httpPost(`database/propertyrooms`, room);
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
      }
    },

    deleteRooms: async (propertyId: number) => {
      try {
        await apiNextClient.httpDelete(
          `properties/${propertyId}/deleterooms`,
          null,
        );
        return true;
      } catch (err) {
        displayErrorMessage(getErrorMessage(err));
        return false;
      }
    },
  };
}
